<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'DateRangeSelector',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'changed']);

  const props = defineProps({
    ...userComponentProps,
    name: {
      type: String,
      required: true,
    },
    from: {
      type: String,
      required: false,
    },
    to: {
      type: String,
      required: false,
    },
    size: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getCurrentWeek = (...args) => {
    const t0 = new Date();
    return [$fn.value.dateFns.startOfWeek(t0, { weekStartsOn: 1 }), null];
  };
  const getCurrentMonth = (...args) => {
    const t0 = new Date();
    return [$fn.value.dateFns.startOfMonth(t0), null];
  };
  const getCurrentYear = (...args) => {
    const t0 = new Date();
    return [$fn.value.dateFns.startOfYear(t0), null];
  };
  const getPreviousWeek = (...args) => {
    const prev_monday = $fn.value.dateFns.previousMonday(
      $fn.value.dateFns.startOfWeek(new Date(), { weekStartsOn: 1 })
    );
    return [prev_monday, $fn.value.dateFns.endOfWeek(prev_monday)];
  };
  const getPreviousMonth = async (...args) => {
    const start = $fn.value.dateFns.sub((await getCurrentMonth())[0], {
      months: 1,
    });
    return [start, $fn.value.dateFns.endOfMonth(start)];
  };
  const getPreviousYear = async (...args) => {
    const start = $fn.value.dateFns.sub((await getCurrentYear())[0], {
      years: 1,
    });
    return [start, $fn.value.dateFns.endOfYear(start)];
  };
  const getPreviousSevenDays = (...args) => {
    const t0 = new Date();
    return [$fn.value.dateFns.sub(t0, { days: 7 }), t0];
  };
  const getPreviousThirtyDays = (...args) => {
    const t0 = new Date();
    return [$fn.value.dateFns.sub(t0, { days: 30 }), t0];
  };
  const getPreviousSixtyDays = (...args) => {
    const t0 = new Date();
    return [$fn.value.dateFns.sub(t0, { days: 60 }), t0];
  };
  const onModeChanged = async (...args) => {
    let key = args;

    if (Array.isArray(key)) {
      key = key[0];
    }
    if (key && typeof key === 'object' && 'data' in key) {
      key = key.data;
    }

    const option = _.find(options.value, { key });
    if (option && option.value) {
      const resolvedValue = await option.value;
      const [start, end] = resolvedValue;

      item.value.start = start
        ? $fn.value.formatDate(start, 'yyyy-MM-dd')
        : null;
      item.value.end = end ? $fn.value.formatDate(end, 'yyyy-MM-dd') : null;
    } else if (option && !option.value) {
      item.value.start = null;
      item.value.end = null;
    }
  };

  const item = ref(null);
  const mode = ref('custom');
  const options = ref([
    {
      key: 'custom',
      name: $fn.value.pgettext('DateRangeSelector', 'Custom'),
    },
    {
      key: 'current_week',
      name: $fn.value.pgettext('DateRangeSelector', 'Current week'),
      value: getCurrentWeek(),
    },
    {
      key: 'current_month',
      name: $fn.value.pgettext('DateRangeSelector', 'Current month'),
      value: getCurrentMonth(),
    },
    {
      key: 'current_year',
      name: $fn.value.pgettext('DateRangeSelector', 'Current year'),
      value: getCurrentYear(),
    },
    {
      key: 'previous_week',
      name: $fn.value.pgettext('DateRangeSelector', 'Previous week'),
      value: getPreviousWeek(),
    },
    {
      key: 'previous_month',
      name: $fn.value.pgettext('DateRangeSelector', 'Previous month'),
      value: getPreviousMonth(),
    },
    {
      key: 'previous_year',
      name: $fn.value.pgettext('DateRangeSelector', 'Previous year'),
      value: getPreviousYear(),
    },
    {
      key: 'previous_seven_days',
      name: $fn.value.pgettext('DateRangeSelector', 'Previous seven days'),
      value: getPreviousSevenDays(),
    },
    {
      key: 'previous_thirty_days',
      name: $fn.value.pgettext('DateRangeSelector', 'Previous thirty days'),
      value: getPreviousThirtyDays(),
    },
    {
      key: 'previous_60_days',
      name: $fn.value.pgettext('DateRangeSelector', 'Previous sixty days'),
      value: getPreviousSixtyDays(),
    },
  ]);
  const ux = ref([]);

  const $data = computed(() => ({
    item: item.value,
    mode: mode.value,
    options: options.value,
    ux: ux.value,
  }));

  const $$load_item = () => {
    return { start: props.from, end: props.to };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'mode', cache: { type: 'local', key: "{{ name + '.mode' }}" } },
      { name: 'options' },
      { name: 'ux' },
    ],
    loaders: { item: $$load_item },
    ux: {},
    dataRefs: { item, mode, options, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.name,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('name');
    }
  );
  watch(
    () => props.from,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('from');
    }
  );
  watch(
    () => props.to,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('to');
    }
  );
  watch(
    () => props.size,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('size');
    }
  );

  const act_6466705138206980_script_kv5y4t5tr_value_callable = async (
    $event
  ) => {
    return await onModeChanged($event);
  };
  const act_6466705138206980_script_kv5y4t5tr_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6466705138206980_script_kv5y4t5tr_value_callable($event);
      },
    };
  };
  const act_6466705138206980_emit_k1k6txsxf_getActionArgs = async ($event) => {
    return {
      event: 'changed',
      value: [item.value.start, item.value.end],
    };
  };
  const act_3943151158818434_emit_e58cpnq3b_getActionArgs = async ($event) => {
    return {
      event: 'changed',
      value: [item.value.start, item.value.end],
    };
  };
  const act_4207795475682514_emit_653r17cru_getActionArgs = async ($event) => {
    return {
      event: 'changed',
      value: [item.value.start, item.value.end],
    };
  };

  const act_7315092382398562_reloadComponentData_oxc2sa3wm =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_hwnm56f89 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_oxc2sa3wm,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_hwnm56f89],
    event: 'reload',
  });
  const act_6466705138206980_script_kv5y4t5tr = new actionTypes.ScriptAction({
    actionArgs: act_6466705138206980_script_kv5y4t5tr_getActionArgs,
  });
  const inst_evh_6466705138206980_input_k2leac854 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_script_kv5y4t5tr,
      event: 'input',
      displayName: 'script',
    });
  const act_6466705138206980_emit_k1k6txsxf = new actionTypes.EmitAction({
    actionArgs: act_6466705138206980_emit_k1k6txsxf_getActionArgs,
  });
  const inst_evh_6466705138206980_input_m69zyyixx =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_emit_k1k6txsxf,
      event: 'input',
      displayName: 'emit',
    });
  const inst_evh_6466705138206980_input = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_input_k2leac854,
      inst_evh_6466705138206980_input_m69zyyixx,
    ],
    event: 'input',
  });
  const act_3943151158818434_emit_e58cpnq3b = new actionTypes.EmitAction({
    actionArgs: act_3943151158818434_emit_e58cpnq3b_getActionArgs,
  });
  const inst_evh_3943151158818434_input_6lqyrtndd =
    new actionTypes.EventHandlerImpl({
      action: act_3943151158818434_emit_e58cpnq3b,
      event: 'input',
      displayName: 'emit',
    });
  const inst_evh_3943151158818434_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3943151158818434_input_6lqyrtndd],
    event: 'input',
  });
  const act_4207795475682514_emit_653r17cru = new actionTypes.EmitAction({
    actionArgs: act_4207795475682514_emit_653r17cru_getActionArgs,
  });
  const inst_evh_4207795475682514_input_hlfjjqwfh =
    new actionTypes.EventHandlerImpl({
      action: act_4207795475682514_emit_653r17cru,
      event: 'input',
      displayName: 'emit',
    });
  const inst_evh_4207795475682514_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4207795475682514_input_hlfjjqwfh],
    event: 'input',
  });

  const actions = {
    inst_evh_7315092382398562_reload_hwnm56f89,
    inst_evh_7315092382398562_reload,
    inst_evh_6466705138206980_input_k2leac854,
    inst_evh_6466705138206980_input_m69zyyixx,
    inst_evh_6466705138206980_input,
    inst_evh_3943151158818434_input_6lqyrtndd,
    inst_evh_3943151158818434_input,
    inst_evh_4207795475682514_input_hlfjjqwfh,
    inst_evh_4207795475682514_input,
  };

  const evh_7315092382398562_reload_hwnm56f89 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_hwnm56f89.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_input_k2leac854 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_input_k2leac854.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_input_m69zyyixx = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_input_m69zyyixx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_input = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_input_6lqyrtndd = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_input_6lqyrtndd.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_input = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4207795475682514_input_hlfjjqwfh = async (event, scope) => {
    return await actions.inst_evh_4207795475682514_input_hlfjjqwfh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4207795475682514_input = async (event, scope) => {
    return await actions.inst_evh_4207795475682514_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="d-flex gap-2" v-if="loaded">
      <div class="form-group m-0">
        <ItpFormNativeSelect
          name="mode"
          v-model="mode"
          :options="options"
          valueField="key"
          textField="name"
          required
          :size="size"
          class="w-auto"
          @input="evh_6466705138206980_input($event, {})"
        >
        </ItpFormNativeSelect>
      </div>
      <div class="form-group m-0">
        <ItpFormDatetime
          name="start"
          type="date"
          v-model="item.start"
          required
          :size="size"
          @input="evh_3943151158818434_input($event, {})"
        >
        </ItpFormDatetime>
      </div>
      <div class="form-group m-0">
        <ItpFormDatetime
          name="end"
          type="date"
          v-model="item.end"
          required
          :size="size"
          clearable
          @input="evh_4207795475682514_input($event, {})"
        >
        </ItpFormDatetime>
      </div>
    </div>
  </ItpUsercontrol>
</template>
